import type React from "react";
import FileUpload, { FileUploadData, FileUploadStateHandler } from "./FileUpload";
import { LucideFolderUp } from "lucide-react";
import { memo } from "react";

export interface IDroppableFileUploadParameters
{
    id?: string;
    accept?: string;
    uploadData: FileUploadData;
    updateState: FileUploadStateHandler;
    accessibleFieldName?: string;
    disabled?: boolean;
    // When true, an already-picked file renders as a green success card (checkmark, filename, size, upload date)
    showSuccessCard?: boolean;
    uploadedAt?: string | null;
    fileSizeBytes?: number | null;
    previewUrl?: string | null;
}

interface IFileLabelParameters
{
    uploadData: FileUploadData;
    accessibleFieldName?: string;
}

const FileUploadLabel: React.FC<IFileLabelParameters> = memo(({ uploadData, accessibleFieldName }) =>
(
    <div className="w-full self-stretch flex flex-col justify-center items-center">
        <div className="flex justify-center items-center">
            <div className="pt-6 pb-2 inline-flex flex-col justify-start items-start gap-1">
                <div className="justify-center text-gray-500 text-base font-semibold font-sora">
                    <span className="underline">
                        Select a file
                    </span>
                    <span> or drag it here</span>
                    <span className="sr-only">
                        {accessibleFieldName}
                    </span>
                </div>
                <div className="w-52 text-center justify-center text-gray-500 text-xs font-normal font-sora">PDF, PNG, or JPG (max. 10MB)</div>

            </div>
            <div className="w-6 h-6 overflow-hidden">
                <LucideFolderUp className="w-5 h-4 mx-auto" color="#6A7785" />
            </div>
        </div>
        {
            (uploadData.filename &&
                (
                    <div className="text-center">
                        <p
                            className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2 inline-block"
                            style={{ fontFamily: "Sora, system-ui, sans-serif" }}
                        >
                            ✓ {uploadData.filename.replace("C:\\fakepath\\", "")}
                        </p>
                    </div>
                ))
            ||
            (
                <div className="pt-4">
                </div>
            )
        }
    </div>
));

const DroppableFileUpload: React.FC<IDroppableFileUploadParameters> = ({
    id,
    accept,
    uploadData,
    updateState,
    accessibleFieldName,
    disabled,
    showSuccessCard,
    uploadedAt,
    fileSizeBytes,
    previewUrl
}) =>
{
    //We don't create state here - it must be handled by the parent control
    const fieldLabel =
        (<FileUploadLabel
            uploadData={uploadData}
            accessibleFieldName={accessibleFieldName}
        />);

    return (
        <FileUpload
            id={id}
            accept={accept}
            uploadData={uploadData}
            updateState={updateState}
            disabled={disabled}

            inputClassOverride="sr-only"
            fieldLabel={fieldLabel}
            className="relative w-full self-stretch px-3 py-5 rounded outline outline-dashed outline-1 outline-offset-[-1px] outline-gray-500 flex justify-center items-center gap-3.5 flex flex-col-reverse"

            droppable
            showSuccessCard={showSuccessCard}
            accessibleFieldName={accessibleFieldName}
            uploadedAt={uploadedAt}
            fileSizeBytes={fileSizeBytes}
            previewUrl={previewUrl}
        />
    );
};

export default DroppableFileUpload;